//@flow
import {ButtonBase, Hidden, Typography} from '@material-ui/core';
import {useLocation} from 'react-router-dom';
import {Close} from '@material-ui/icons';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useState} from 'react';
import {colors} from '../../assets/styles/colors';
import CustomButton from '../CustomButton/CustomButton';

import girlSupport from '../../assets/img/girl-support.png';
import {useDaysElapsed} from '../../modules/tiers/utils';
import {useOnboardingProgress} from '../../modules/onboarding-config/utils';
import {TIENDADA_LINKS} from '../../constants/tiendada-links';
import {useTierStoreName} from '../../modules/store/utils/utils_tier';
import {FREE_TRIAL_TIERS} from '../../entities';

const useStyles = makeStyles(theme => ({
  container: {
    height: 'fit-content',
    zIndex: 1300,
    width: '100%',
    padding: '1rem',

    [theme.breakpoints.up('sm')]: {
      padding: '1.5rem',
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    background: 'linear-gradient(89.92deg, #18003D -15.03%, #37127A 90.58%)',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    alignContent: 'center',
    justifyContent: 'space-between',
    padding: '.75rem',
    color: '#FFF',
    width: '100%',
    position: 'relative',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(2, 3),
    },
  },
  header: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexGrow: 1,
  },
  details: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      gap: theme.spacing(1),
    },
  },
  avatar: {
    minWidth: '48px',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    position: 'relative',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      background: '#05B686',

      [theme.breakpoints.up('md')]: {
        width: '12px',
        height: '12px',
      },
    },

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: 'inherit',
    },

    [theme.breakpoints.up('md')]: {
      minWidth: '36px',
      width: '36px',
      height: '36px',
    },
  },
  title: {
    color: 'white',
    fontSize: '.75rem',
    fontWeight: 600,

    [theme.breakpoints.up('lg')]: {
      paddingRight: theme.spacing(1),
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRight: `1px solid ${colors.grey300}`,
    },
  },
  description: {
    fontSize: '.75rem',
    fontWeight: 500,
    [theme.breakpoints.up('md')]: {
      fontWeight: 400,
    },
  },
  buttons: {
    display: 'flex',

    gap: theme.spacing(1),
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },

    [theme.breakpoints.up('md')]: {
      width: '80%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '30%',
    },
  },
  whatsAppBtn: {
    background: '#25D366',
    borderRadius: '12px',
    color: '#FFF',
    width: '100%',

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 1.5),
    },
  },
  helpBtn: {
    width: '100%',
    background: '#F7D144',
    color: '#18003D',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '.75rem',
    padding: '.5rem 1.5rem',

    '&:hover': {
      color: '#F7D144',
      background: '#f7d1449c',
    },
  },
  closeButton: {
    position: 'absolute',
    top: '-1rem',
    right: '-1rem',
    padding: '.5rem',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: colors.grey200,
    color: colors.primary,
  },
}));

const SHOW_CONSULTANCY_ALERT_KEY = 'SHOW_CONSULTANCY_ALERT_KEY';

export default function ConsultancyPageAlert() {
  const classes = useStyles();

  const progress = useOnboardingProgress();

  const tierName = useTierStoreName();

  const [show, setShow] = useState(() => {
    //To don't show the alert after the 23 days
    const saved = localStorage.getItem(SHOW_CONSULTANCY_ALERT_KEY);
    if (!saved) return true;
    return false;
  });

  const location = useLocation();

  const {daysElapsed} = useDaysElapsed();

  useEffect(() => {
    if (daysElapsed >= 23) {
      setShow(false);
      localStorage.setItem(SHOW_CONSULTANCY_ALERT_KEY, 'true');
    }
  }, [daysElapsed]);

  useEffect(() => {
    setShow(true);
  }, [location.pathname]);

  if (!FREE_TRIAL_TIERS.includes(tierName)) return null;
  if (progress < 100) return null;
  if (!show) return null;

  return (
    <div className={classes.container}>
      <div className={classes.root}>
        <div className={classes.header}>
          <Hidden smDown>
            <Typography className={classes.title}>¿Necesitas ayuda?</Typography>
          </Hidden>
          <picture className={classes.avatar}>
            <img src={girlSupport} alt="" />
          </picture>
          <div className={classes.details}>
            <Hidden mdUp>
              <Typography className={classes.title}>
                ¿Necesitas ayuda?
              </Typography>
            </Hidden>
            <Typography className={classes.description}>
              Estamos aquí para ayudarte a crear una tienda virtual increíble
            </Typography>
          </div>
        </div>

        <div className={classes.buttons}>
          <CustomButton
            className={classes.helpBtn}
            href={TIENDADA_LINKS.customerSupport.consultancy}
            target="_blank"
            rel="noopener noreferrer"
          >
            Agenda una asesoría
          </CustomButton>
        </div>

        <ButtonBase
          className={classes.closeButton}
          onClick={() => setShow(false)}
        >
          <Close fontSize="small" />
        </ButtonBase>
      </div>
    </div>
  );
}
